import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import { RequestStatusObject, buildReducers, createLazyList, createRequestStatusObject } from '../utils';
import { LazyList, LazyListRequest, LazyListResponse } from '../../types/types';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import i18n from '../../i18n';
import { SharedDocumentsRequest, SharedDocumentsTemplate } from '../../types/sharedDocuments';
import { readOrganisation } from './organisation';
import dayjs from 'dayjs';
import { readUser } from './user';
import { getDocuments } from './document';
import { Button } from '@mui/material';
import React from 'react';

const initialState: {
	currentRequest: SharedDocumentsRequest | undefined;
	currentTemplate: SharedDocumentsTemplate | undefined;
	userRequestsList: SharedDocumentsRequest[] | undefined;
	organisationRequestsList: LazyList<SharedDocumentsRequest>;
	organisationTemplatesList: LazyList<SharedDocumentsTemplate>;
	notificationsList: any[];
	createTemplate: RequestStatusObject;
	updateTemplate: RequestStatusObject;
	readRequest: RequestStatusObject;
	listRequest: RequestStatusObject;
	listTemplateRequest: RequestStatusObject;
	createRequest: RequestStatusObject;
	updateRequest: RequestStatusObject;
	notificationsRequest: RequestStatusObject;
	deleteNotificationRequest: RequestStatusObject;
	completeRequest: RequestStatusObject;
	dismissRequest: RequestStatusObject;
} = {
	currentRequest: undefined,
	currentTemplate: undefined,
	userRequestsList: undefined,
	organisationRequestsList: createLazyList(),
	organisationTemplatesList: createLazyList(),
	notificationsList: [],
	createTemplate: createRequestStatusObject(),
	updateTemplate: createRequestStatusObject(),
	readRequest: createRequestStatusObject(),
	listRequest: createRequestStatusObject(),
	listTemplateRequest: createRequestStatusObject(),
	createRequest: createRequestStatusObject(),
	updateRequest: createRequestStatusObject(),
	notificationsRequest: createRequestStatusObject(),
	deleteNotificationRequest: createRequestStatusObject(),
	completeRequest: createRequestStatusObject(),
	dismissRequest: createRequestStatusObject()
};

export const listTemplates = createAsyncThunk<
	LazyListResponse<SharedDocumentsTemplate>,
	{ organisationId: string } & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>('sharedDocumentsTemplates/list', async ({ organisationId, limit, next }) => {
	const result = await API.get('inspirusapi', `/organisations/${organisationId}/shared-documents-templates`, {
		headers: {},
		response: true,
		queryStringParameters: {
			limit,
			next
		}
	});
	return result.data;
});

type ListRequestFilters = {
	startDate?: string | null;
	endDate?: string | null;
	overdue?: string | null;
	status?: string | null;
	requestStatus?: string | null;
};

export const listRequests = createAsyncThunk<
	LazyListResponse<SharedDocumentsRequest>,
	{ organisationId: string } & LazyListRequest & ListRequestFilters,
	{ dispatch: AppDispatch; state: RootState }
>(
	'sharedDocumentsRequests/list',
	async ({ organisationId, limit, next, startDate, endDate, overdue, status, requestStatus }) => {
		let qs = '';
		if (startDate && endDate) qs = `?startDate=${startDate}&endDate=${endDate}`;
		if (overdue) qs = `?overdue=${overdue}`;
		if (status) qs = `?status=${status}`;
		const result = await API.get('inspirusapi', `/organisations/${organisationId}/shared-documents-requests${qs}`, {
			headers: {},
			response: true,
			queryStringParameters: {
				limit,
				next,
				status,
				requestStatus
			}
		});
		return result.data;
	}
);

export const listNotifications = createAsyncThunk<
	any[],
	{ organisationId: string; sharedDocumentRequestId?: string },
	{ dispatch: AppDispatch; state: RootState }
>('sharedDocumentsNotifications/list', async ({ organisationId, sharedDocumentRequestId }) => {
	const qs = sharedDocumentRequestId ? `?sharedDocumentRequestId=${sharedDocumentRequestId}` : '';
	const result = await API.get(
		'inspirusapi',
		`/organisations/${organisationId}/shared-documents-requests-notifications${qs}`,
		{
			headers: {},
			response: true
		}
	);
	return result.data.notifications;
});

export const listUserRequests = createAsyncThunk<
	SharedDocumentsRequest[],
	{ userId: string },
	{ dispatch: AppDispatch; state: RootState }
>('sharedDocumentsUserRequests/list', async ({ userId }) => {
	const result = await API.get('inspirusapi', `/users/${userId}/shared-documents-requests`, {
		headers: {},
		response: true
	});
	return result.data;
});

export const readRequest = createAsyncThunk<
	SharedDocumentsRequest,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('sharedDocumentsRequests/read', async (requestId, { getState, dispatch }) => {
	const result = await API.get('inspirusapi', `/shared-documents-requests/${requestId}/`, {
		headers: {},
		response: true
	});

	const request: SharedDocumentsRequest = result.data;

	const organisation = getState().organisation.current;
	if (organisation?.id !== request.organisationId) {
		dispatch(readOrganisation(request.organisationId));
	}

	const user = getState().user.currentUser;
	if (user?.id !== request.userId) {
		dispatch(readUser({ id: request.userId }));
	}
	const docIds = request.stepsData?.map((step) => step.documentId) ?? [];
	dispatch(getDocuments(docIds));

	return request;
});

export const readTemplate = createAsyncThunk<
	SharedDocumentsTemplate,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('sharedDocumentsTemplates/read', async (templateId) => {
	const result = await API.get('inspirusapi', `/shared-documents-templates/${templateId}`, {
		headers: {},
		response: true
	});
	return result.data;
});

export const createTemplate = createAsyncThunk<
	SharedDocumentsTemplate,
	Partial<SharedDocumentsTemplate>,
	{ dispatch: AppDispatch; state: RootState }
>('sharedDocumentsTemplates/createTemplate', async (template) => {
	const result = await API.post('inspirusapi', '/shared-documents-templates/', {
		headers: {},
		response: true,
		body: template
	});
	return result.data;
});

export const updateTemplate = createAsyncThunk<
	SharedDocumentsTemplate,
	Partial<SharedDocumentsTemplate>,
	{ dispatch: AppDispatch; state: RootState }
>('sharedDocumentsTemplates/update', async (template) => {
	const result = await API.put('inspirusapi', `/shared-documents-templates/${template.id}`, {
		headers: {},
		response: true,
		body: template
	});
	return result.data;
});

export const createRequest = createAsyncThunk<
	SharedDocumentsRequest,
	Partial<SharedDocumentsRequest>,
	{ dispatch: AppDispatch; state: RootState }
>('sharedDocumentsRequests/createRequest', async (request) => {
	const result = await API.post('inspirusapi', '/shared-documents-requests/', {
		headers: {},
		response: true,
		body: request
	});
	return result.data;
});

export const updateRequest = createAsyncThunk<
	SharedDocumentsRequest,
	Partial<SharedDocumentsRequest>,
	{ dispatch: AppDispatch; state: RootState }
>('sharedDocumentsRequests/update', async (request) => {
	const result = await API.put('inspirusapi', `/shared-documents-requests/${request.id}`, {
		headers: {},
		response: true,
		body: { stepsData: request.stepsData }
	});
	return result.data;
});

export const completeRequest = createAsyncThunk<
	SharedDocumentsRequest,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('sharedDocumentsRequests/complete', async (requestId) => {
	const result = await API.put('inspirusapi', `/shared-documents-requests/${requestId}/status`, {
		headers: {},
		response: true,
		body: { status: 'complete' }
	});
	return result.data;
});

export const dismissRequest = createAsyncThunk<
	SharedDocumentsRequest,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('sharedDocumentsRequests/dismiss', async (requestId) => {
	const result = await API.put('inspirusapi', `/shared-documents-requests/${requestId}/status`, {
		headers: {},
		response: true,
		body: { status: 'dismissed' }
	});
	return result.data;
});

export const deleteNotification = createAsyncThunk<void, { id: string }, { dispatch: AppDispatch; state: RootState }>(
	'sharedDocumentsNotifications/delete',
	async ({ id }) => {
		// Encode the # character
		const encodedId = id.replace('#', '%23');
		await API.del('inspirusapi', `/organisations/shared-documents-requests-notifications/${encodedId}`, {
			headers: {},
			response: true
		});
	}
);

const sharedDocumentsSlice = createSlice({
	name: 'sharedDocuments',
	initialState,
	reducers: {
		resetVerification: (state) => {
			state = initialState;
			return state;
		},
		updateCurrentRequestData: (state, action) => {
			if (state.currentRequest) {
				state.currentRequest.stepsData = action.payload;
			}
			return state;
		},
		addCurrentRequestData: (state, action) => {
			if (state.currentRequest) {
				if (!state.currentRequest.stepsData) {
					state.currentRequest.stepsData = [];
				}
				const index = state.currentRequest.stepsData.findIndex(
					({ stepNumber }) => stepNumber === action.payload.stepNumber
				);
				if (index >= 0) {
					state.currentRequest.stepsData[index] = {
						documentId: action.payload.documentId,
						stepNumber: action.payload.stepNumber,
						submissionDateTime: dayjs().toString()
					};
				} else {
					state.currentRequest.stepsData.push({
						documentId: action.payload.documentId,
						stepNumber: action.payload.stepNumber,
						submissionDateTime: dayjs().toString()
					});
				}
			}
			return state;
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, listTemplates, 'listTemplateRequest', {
			onFulfilled: (state, payload) => {
				const list = state.organisationTemplatesList;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
			}
		});
		buildReducers(builder, listRequests, 'listRequest', {
			onFulfilled: (state, payload) => {
				const list = state.organisationRequestsList;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
			}
		});
		buildReducers(builder, listUserRequests, 'listRequest', {
			onFulfilled: (state, payload) => {
				state.userRequestsList = payload;
			}
		});
		buildReducers(builder, readRequest, 'readRequest', {
			onFulfilled: (state, payload) => {
				state.currentRequest = payload;
			}
		});
		buildReducers(builder, readTemplate, 'readRequest', {
			onFulfilled: (state, payload) => {
				state.currentTemplate = payload;
			}
		});

		buildReducers(builder, createTemplate, 'createTemplate', {
			onPending: (state) => {
				state.createTemplate.isLoading = true;
			},
			onFulfilled: (state, payload) => {
				state.createTemplate.isLoading = false;
				state.organisationTemplatesList = { ...state.organisationTemplatesList, payload };
				enqueueSnackbar(i18n.t('pages.organisation.sharedDocumentsTemplates.create.successMessage'), {
					variant: 'success',
					persist: true,
					action: (id) =>
						React.createElement(
							Button,
							{
								sx: { textTransform: 'none' },
								onClick: () => closeSnackbar(id)
							},
							'Close'
						)
				});
			},
			onRejected: (state) => {
				state.createTemplate.isLoading = false;
				enqueueSnackbar(i18n.t('pages.organisation.sharedDocumentsTemplates.create.errorMessage'), {
					variant: 'error'
				});
			}
		});

		buildReducers(builder, createRequest, 'createRequest', {
			onPending: (state) => {
				state.createRequest.isLoading = true;
			},
			onFulfilled: (state) => {
				state.createRequest.isLoading = false;
				enqueueSnackbar(i18n.t('pages.organisation.sharedDocumentsRequests.create.successMessage'), {
					variant: 'success',
					persist: true,
					action: (id) =>
						React.createElement(
							Button,
							{
								sx: { textTransform: 'none' },
								onClick: () => closeSnackbar(id)
							},
							'Close'
						)
				});
			},
			onRejected: (state) => {
				state.createRequest.isLoading = false;
				enqueueSnackbar(i18n.t('pages.organisation.sharedDocumentsRequests.create.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, updateTemplate, 'updateRequest', {
			onPending: (state) => {
				state.updateTemplate.isLoading = true;
			},
			onFulfilled: (state) => {
				state.updateTemplate.isLoading = false;
				enqueueSnackbar(i18n.t('pages.organisation.sharedDocumentsTemplates.update.successMessage'), {
					variant: 'success'
				});
			},
			onRejected: (state) => {
				state.updateTemplate.isLoading = false;
				enqueueSnackbar(i18n.t('pages.organisation.sharedDocumentsTemplates.update.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, listNotifications, 'notificationsRequest', {
			onFulfilled: (state, payload) => {
				state.notificationsList = payload;
			}
		});
		buildReducers(builder, deleteNotification, 'deleteNotificationRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('Notification deleted successfully'), {
					variant: 'success'
				});
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('Failed to delete notification'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, completeRequest, 'completeRequest', {
			onFulfilled: (state, payload) => {
				state.currentRequest = payload;
				enqueueSnackbar(i18n.t('Request marked as complete successfully'), { variant: 'success' });
			}
		});
		buildReducers(builder, dismissRequest, 'dismissRequest', {
			onFulfilled: (state, payload) => {
				state.currentRequest = payload;
				enqueueSnackbar(i18n.t('Request dismissed successfully'), { variant: 'success' });
			}
		});
	}
});

export const { resetVerification, updateCurrentRequestData, addCurrentRequestData } = sharedDocumentsSlice.actions;
export default sharedDocumentsSlice.reducer;
